import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DurationTimer from './DurationTimer';
import { copyTextToClipboard } from '../utils/clipboard';
import {
  formatResearchDuration,
  formatResearchToolOutputAudit,
} from '../utils/researchCopyFormat';

const ACTIVE = new Set(['queued', 'running']);

function prettyPayload(payload) {
  if (payload == null) return '';
  if (typeof payload === 'string') return payload;
  try {
    return JSON.stringify(payload, null, 2);
  } catch (_) {
    return String(payload);
  }
}

/**
 * Collapsible Deep Research debug trail — same interaction pattern as chat Thinking History.
 */
export default function ResearchToolOutputBlock({ job, steps = [], defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const [copyNotice, setCopyNotice] = useState('');
  const copyNoticeTimerRef = useRef(null);
  const listRef = useRef(null);

  const items = useMemo(() => {
    const arr = Array.isArray(steps) ? [...steps] : [];
    arr.sort((a, b) => {
      const an = Number(a.stepNumber);
      const bn = Number(b.stepNumber);
      if (Number.isFinite(an) && Number.isFinite(bn) && an !== bn) return an - bn;
      return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
    });
    return arr;
  }, [steps]);

  const lastItem = items.length > 0 ? items[items.length - 1] : null;
  const isActive = ACTIVE.has(String(job?.status || '').toLowerCase());
  const startAt = job?.startedAt || job?.createdAt;
  const endAt = job?.completedAt || job?.updatedAt;

  const showCopyNotice = useCallback((label) => {
    if (copyNoticeTimerRef.current) clearTimeout(copyNoticeTimerRef.current);
    setCopyNotice(label);
    copyNoticeTimerRef.current = setTimeout(() => {
      setCopyNotice('');
      copyNoticeTimerRef.current = null;
    }, 2000);
  }, []);

  useEffect(() => () => {
    if (copyNoticeTimerRef.current) clearTimeout(copyNoticeTimerRef.current);
  }, []);

  useEffect(() => {
    if (open && listRef.current) {
      const id = requestAnimationFrame(() => {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      });
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [open, items]);

  const handleCopy = useCallback(async (e) => {
    e.stopPropagation();
    try {
      const text = formatResearchToolOutputAudit(items, { job });
      if (!text.trim()) {
        showCopyNotice('No tool output yet');
        return;
      }
      const ok = await copyTextToClipboard(text);
      showCopyNotice(ok ? 'Tool output copied' : 'Copy failed');
    } catch (err) {
      showCopyNotice(err?.message || 'Copy failed');
    }
  }, [items, job, showCopyNotice]);

  const toggle = useCallback(() => setOpen((v) => !v), []);

  return (
    <div className="research-debug-block request-block">
      <div
        className="req-header"
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <div className="req-title">
          <strong>Tool Output</strong>
          <div className="req-meta">
            Status:{' '}
            <span style={{ textTransform: 'capitalize' }}>{job?.status || 'unknown'}</span>
            {' • Time: '}
            {isActive ? (
              <DurationTimer start={startAt} />
            ) : (
              formatResearchDuration(startAt, endAt)
            )}
            {copyNotice ? <span className="req-copy-notice"> • {copyNotice}</span> : null}
          </div>
        </div>
        <div
          className="req-header-actions"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="req-copy-btn req-copy-btn-icon"
            title="Copy tool output"
            aria-label="Copy tool output"
            onClick={handleCopy}
          >
            <i className="fas fa-copy" />
          </button>
          <div
            className="chevron-btn"
            aria-label={open ? 'Hide tool output' : 'Show tool output'}
          >
            <i className={`fas fa-chevron-right ${open ? 'rotated' : ''}`} />
          </div>
        </div>
      </div>

      <div className={`history-outer ${open ? 'open' : ''}`}>
        {open && (
          <div ref={listRef} className="history-inner research-debug-inner">
            {job?.error ? (
              <div className="research-debug-error" role="alert">
                <i className="fas fa-triangle-exclamation" aria-hidden="true" /> {job.error}
              </div>
            ) : null}
            {(() => {
              const errStep = items.find((s) => s.rawPayload?.category === 'llm_headers_timeout')
                || [...items].reverse().find((s) => String(s.actionType || '').toLowerCase() === 'error');
              const suggestions = errStep?.rawPayload?.suggestions;
              const progress = errStep?.rawPayload?.progress;
              const retry = errStep?.rawPayload?.retryDiagnostics;
              if (!suggestions?.length && !retry && progress == null) return null;
              return (
                <div className="research-debug-suggestions">
                  {progress && progress.enteredAgentLoop === false ? (
                    <p>Agent loop never started (no tokens / tool calls received).</p>
                  ) : null}
                  {retry?.attemptCount ? (
                    <p>
                      SDK attempts: {retry.attemptCount}
                      {retry.approxPerAttemptMs
                        ? ` (~${Math.round(retry.approxPerAttemptMs / 1000)}s each)`
                        : ''}
                      {Number.isFinite(errStep?.rawPayload?.elapsedMs)
                        ? ` · elapsed ${Math.round(errStep.rawPayload.elapsedMs / 1000)}s`
                        : ''}
                      {' · '}not the Deep Research job timeout
                      {errStep?.rawPayload?.timeoutMinutes
                        ? ` (${errStep.rawPayload.timeoutMinutes}m)`
                        : ''}.
                    </p>
                  ) : null}
                  {Array.isArray(suggestions) && suggestions.length ? (
                    <ul>
                      {suggestions.map((s) => (
                        <li key={s}>{s}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              );
            })()}
            <ul className="research-debug-list">
              {items.map((step) => {
                const payloadText = prettyPayload(step.rawPayload);
                const showPayload =
                  payloadText &&
                  payloadText !== '{}' &&
                  payloadText !== 'null' &&
                  payloadText !== '""';
                const isErrorStep =
                  String(step.actionType || '').toLowerCase() === 'error' ||
                  /fail|abort|timeout|error/i.test(String(step.summary || ''));
                return (
                  <li
                    key={step.id || `${step.stepNumber}-${step.createdAt}`}
                    className={isErrorStep ? 'research-debug-item--error' : undefined}
                  >
                    <div className="research-debug-item-head">
                      <span className="research-debug-step-num">#{step.stepNumber}</span>
                      <span className="research-debug-action">{step.actionType}</span>
                      <span className="research-debug-summary">{step.summary}</span>
                    </div>
                    {showPayload ? (
                      <pre className="research-debug-payload">{payloadText}</pre>
                    ) : null}
                  </li>
                );
              })}
              {items.length === 0 && (
                <li className="research-debug-empty">No tool output yet</li>
              )}
            </ul>
          </div>
        )}
      </div>

      {!open && lastItem ? (
        <div className="history-footer" aria-live="polite" aria-atomic="true">
          <div className="hf-item">
            <span className="hf-label">Last:</span>
            <span className="hf-text">
              {lastItem.actionType}: {lastItem.summary}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
